import React, {useState, useEffect} from 'react';
import {Button, OverlayTrigger, Tooltip, Toast} from 'react-bootstrap';
import { Ban, Flag, Trash2, Check} from 'lucide-react';
import {apiClient} from '../api/ApiClient';

const EmailActions = ({ 
  memberId,
  emailId, 
  onBlock, 
  onReport,
  onDelete,
  showDelete = true 
}) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isReported, setIsReported] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [blockSuccess, setBlockSuccess] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [loading, setLoading] = useState({
      block: false,
      report: false,
      delete: false
  });

  const [notification, setNotification] = useState({
      show: false,
      message: '',
      type: 'success'
  });

  const buttonStyle = {
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '8px 16px',
      minWidth: '100px'
  };

  const successStyle = {
      ...buttonStyle,
      color: '#28a745'
  };

  // Reset states when memberId changes
  useEffect(() => {
      setIsBlocked(false);
      setIsReported(false);
      setIsDeleted(false);
      setBlockSuccess(false);
      setReportSuccess(false);
      setDeleteSuccess(false);
      setLoading({ block: false, report: false, delete: false });
  }, [memberId]);

  const showNotification = (message, type = 'success') => {
      setNotification({
          show: true,
          message,
          type
      });
      setTimeout(() => {
          setNotification(prev => ({...prev, show: false}));
      }, 3000);
  };

  const handleDeleteEmail = async () => {
      if (isDeleted || loading.delete) return;
      setLoading(prev => ({...prev, delete: true}));
      
      try {
          const response = await apiClient.post('/rest/deleteMail', {
              emailIds: [emailId.toString()],
              memberId: null
          });

          if (response.status === 200) {
              setIsDeleted(true);
              setDeleteSuccess(true);
              showNotification('Email deleted successfully');
              onDelete && onDelete(emailId);
              setTimeout(() => {
                  setDeleteSuccess(false);
              }, 3000);
          }
      } catch (error) {
          console.error('Error deleting email:', error);
          showNotification('Failed to delete email', 'danger');
      } finally {
          setLoading(prev => ({...prev, delete: false}));
      }
  };

  const handleBlockMember = async () => {
      if (isBlocked || loading.block) return;
      setLoading(prev => ({...prev, block: true}));
      
      try {
          const response = await apiClient.post('/rest/blockMember', {
              blockedMemberId: memberId
          });

          if (response.status === 200) {
              setIsBlocked(true);
              setBlockSuccess(true);
              showNotification('Member blocked successfully');
              onBlock && onBlock(memberId);
              setTimeout(() => {
                  setBlockSuccess(false);
              }, 3000);
          }
      } catch (error) {
          console.error('Error blocking member:', error);
          showNotification('Failed to block member', 'danger');
      } finally {
          setLoading(prev => ({...prev, block: false}));
      }
  };

  const handleReportMember = async () => {
      if (isReported || loading.report) return;
      setLoading(prev => ({...prev, report: true}));
      
      try {
          const response = await apiClient.post('/rest/reportMember', {
              reportedMemberId: memberId
          });

          if (response.status === 200) {
              setIsReported(true);
              setReportSuccess(true);
              showNotification('Member reported successfully');
              onReport && onReport(memberId);
              setTimeout(() => {
                  setReportSuccess(false);
              }, 3000);
          }
      } catch (error) {
          console.error('Error reporting member:', error);
          showNotification('Failed to report member', 'danger');
      } finally {
          setLoading(prev => ({...prev, report: false}));
      }
  };

  return (
      <div className="position-relative">
          <div className="d-flex align-items-center">
              {showDelete && (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{isDeleted ? 'Deleted' : 'Delete'}</Tooltip>}
                >
                    <Button
                        variant="link"
                        style={isDeleted ? successStyle : buttonStyle}
                        onClick={handleDeleteEmail}
                        disabled={isDeleted || loading.delete}
                    >
                        {isDeleted ? <Check size={25} /> : <Trash2 size={25} />}
                    </Button>
                </OverlayTrigger>
              )}

              <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{isBlocked ? 'Blocked' : 'Block'}</Tooltip>}
              >
                  <Button
                      variant="link"
                      style={isBlocked ? successStyle : buttonStyle}
                      onClick={handleBlockMember}
                      disabled={isBlocked || loading.block}
                  >
                      {isBlocked ? <Check size={25} /> : <Ban size={25} />}
                  </Button>
              </OverlayTrigger>

              <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{isReported ? 'Reported' : 'Report'}</Tooltip>}
              >
                  <Button
                      variant="link"
                      style={isReported ? successStyle : buttonStyle}
                      onClick={handleReportMember}
                      disabled={isReported || loading.report}
                  >
                      {isReported ? <Check size={25} /> : <Flag size={25} />}
                  </Button>
              </OverlayTrigger>
          </div>

          <div style={{
              position: 'absolute',
              top: -60,
              right: 0,
              left: 0,
              display: 'flex',
              justifyContent: 'center',
              zIndex: 1000
          }}>
              <Toast
                  show={notification.show}
                  onClose={() => setNotification(prev => ({...prev, show: false}))}
                  bg={notification.type}
                  className="text-white"
                  style={{minWidth: '200px'}}
              >
                  <Toast.Body className="text-center">{notification.message}</Toast.Body>
              </Toast>
          </div>
      </div>
  );
};

export default EmailActions;